import clone from 'root-core/utils/object/clone.js'
import {sum, sortBy, zip} from 'lodash-es'
import earliestPromoPaymentDate from 'root-packages/promotions/earliestPromoPaymentDate.js'
import absoluteDiscountAmount from './absoluteDiscountAmount.js'
import roundMoney from './roundMoney.js'
import {applicableSupplementSelector} from './applicableSupplementReduction.js'

export {
  airPromoAmount,
  breakdownPromotionsWithoutTravelProtection,
  breakdownTravelProtectionPromotion,
  notCombinableWith,
  pricePromotion,
  productForPromoAddedToOrder,
  promotionsIncludingTravelProtection,
}

function calculatePaymentDates (promotion, departure, bookingDate) {
  let {paymentBy, depositPaymentBy} = promotion
  if (paymentBy) {
    paymentBy = {
      ...paymentBy,
      date: earliestPromoPaymentDate(paymentBy, departure, bookingDate),
    }
  }

  if (depositPaymentBy) {
    depositPaymentBy = {
      ...depositPaymentBy,
      date: earliestPromoPaymentDate(depositPaymentBy, departure, bookingDate),

    }

    if (paymentBy && paymentBy.date <= depositPaymentBy.date) {
      depositPaymentBy = undefined
    }
  }

  return {paymentBy, depositPaymentBy}
}

function breakdownPromotionsWithoutTravelProtection ({
  promotions,
  passengers,
  departure,
  transfers,
  flights,
  promisedFlights,
  extraStays,
  bookingDate,
}) {
  if (!promotions) return {applied: [], amount: 0}

  const pricedPromotions = promotions
    .filter(promotion => productForPromoAddedToOrder({
      promotion,
      passengers,
      transfers,
      departure,
      extraStays,
    }))
    .map(promotion => pricePromotion({
      promotion,
      passengers,
      departure,
      transfers,
      extraStays,
      flights,
      promisedFlights,
      bookingDate,
    }))
    .filter(p => p && (p.code || p.inlineOffer))

  const bestCombination = bestCombinationOfPromotionsIncludingEpd(
    pricedPromotions
  )

  return {
    applied: bestCombination,
    unapplied: pricedPromotions.filter(p => !bestCombination.find(applied => applied.code === p.code)),
    amount: sum(bestCombination.map(p => p.amount)),
    amountPerPassenger: zip(
      ...bestCombination.map(p => p.amountPerPassenger.map(a => a.amount))
    ).map(promoAmounts =>({amount: sum(promoAmounts)})),
  }
}

function pricePromotion ({
  promotion,
  passengers,
  departure,
  transfers,
  flights,
  bookingDate,
  extraStays,
}) {
  var promoAmount = 0
  var amountPerPassenger
  if(promotion.promotionSubtype === 'ADV') {
    amountPerPassenger = passengers.map((passenger, index) => {
      return {
        amount: index === 0 ? promotion.fixedAmount : 0,
        passengerId: passenger.passengerId,
      }
    })
    promoAmount = promotion.fixedAmount
  }
  else if(promotion.promotionSubtype === 'REF') {
    amountPerPassenger = passengers.map((passenger) => {
      if(!passenger.pastGuestBenefit && !passenger.frequentTravellerDiscount && !passenger.applyAdvocatePromo) {
        return {
          amount: promotion.fixedAmount,
          passengerId: passenger.passengerId,
        }
      }
      return {
        amount: 0,
        passengerId: passenger.passengerId,
      }
    })
    promoAmount = sum(amountPerPassenger.map(a => a.amount))
  }
  else if (promotion.isAirPromotion) {
    promoAmount = airPromoAmount(promotion, flights, passengers.length)
    amountPerPassenger = passengers.map((passenger) => {
      return {
        amount: promoAmount / passengers.length,
        passengerId: passenger.passengerId,
      }
    })
  } else if (promotionIsForTheTourProduct(promotion, departure)) {
    amountPerPassenger = passengers.map((passenger) => {
      const {discountReduction} = applicableSupplementSelector({
        supplementAdjustments: departure.prices.roomSupplementAdjustments,
        roomType: passenger.roomType,
        bookingDate,
      })
      const roomPrice = discountReduction
        ? passenger.basicRoomPrice - passenger.applicableRoomTypeSupplement
        : passenger.basicRoomPrice
      const totalDiscountAmount = absoluteDiscountAmount(roomPrice, promotion)
      const supplementPartOfPromotion = promotion.discountType === 'fixedAmount'
        ? 0
        : absoluteDiscountAmount(passenger.applicableRoomTypeSupplement, promotion)
      const supplementsPromotions = passenger.supplementsPromotions || []
      if (!supplementsPromotions.find(promo => promo.code === promotion.code)) {
        passenger.supplementsPromotions = [...supplementsPromotions, {
          code: promotion.code,
          type: promotion.discountType,
          amount: Math.round(supplementPartOfPromotion),
        }]
      }
      return {
        amount: totalDiscountAmount,
        passengerId: passenger.passengerId,
      }
    })
    promoAmount = sum(amountPerPassenger.map(a => a.amount))
  } else {
    const mandatoryAmount = applyMandatoryProductPromotions(
      passengers,
      departure.prices.mandatoryProducts,
      promotion
    )
    const transfersAmount = applyTransferPromotions(passengers, transfers, promotion)
    const accommodationAmount = applyAccommodationPromotions(passengers, extraStays, promotion)
    promoAmount = mandatoryAmount + transfersAmount + accommodationAmount
    amountPerPassenger = passengers.map((passenger) => {
      return {
        amount: promoAmount / passengers.length,
        passengerId: passenger.passengerId,
      }
    })
  }

  if (!promoAmount && !isZeroValuePromotion(promotion, departure)) {
    return
  }

  const pricedPromotion = {
    id: promotion.id,
    notes: promotion.notes,
    code: promotion.code,
    name: promotion.name,
    products: promotion.isAirPromotion
      ? [{productCode: 'AIRPROD'}]
      : promotion.products,
    amount: promoAmount,
    commissionAmount: promotion.commissionAmount,
    automatic: promotion.automatic || promotion.isAirPromotion,
    isAirPromotion: promotion.isAirPromotion,
    combinable: promotion.combinable,
    epdCombinable: promotion.epdCombinable,
    combinabilityGroups: promotion.combinabilityGroups || [],
    depositAmount: promotion.depositAmount,
    promotionSubtype: promotion.promotionSubtype,
    depositPercentFlag: promotion.depositPercentFlag,
    inlineOffer: promotion.inlineOffer,
    earlyBirdDepartureDaysId: promotion.earlyBirdDepartureDaysId,
    epd: promotion.epd,
    amountPerPassenger,
    discountType: promotion.discountType,
    percent: promotion.percent,
    fixedAmount: promotion.fixedAmount,
    bookingDateWindow: promotion.bookingDateWindow,
    type: promotion.promotionSubtype === 'LMD' ? 'lastMinute' : 'promotion',
    ...calculatePaymentDates(promotion, departure, bookingDate),
  }

  return pricedPromotion
}

function isZeroValuePromotion (promotion, departure) {
  return (
    !promotion.isAirPromotion &&
    promotion.products.some(p => p.productCode === departure.productCode) &&
    promotion.fixedAmount === 0
  )
}

function getBasePromotionValue (promotion, fareValue, partySize) {
  return (promotion.discountType === 'percent')
    ? roundMoney(fareValue * promotion.percent / 100)
    : promotion.fixedAmount * partySize
}

function airPromoAmount (promotion, flights, partySize) {
  if (!flights) return 0

  const fareValue = flights.breakdown.fare || flights.breakdown.calculatedBaseFareAmount
  const basePromotionValue = getBasePromotionValue(promotion, fareValue, partySize)

  const airTaxExemptAmount = promotion.airTaxExempt
    ? flights.breakdown.tax || flights.breakdown.totalAirTax
    : 0
  const fuelSurchargeExemptAmount = promotion.fuelSurchargeExempt
    ? flights.breakdown.fuelSurcharge
    : 0

  return Math.min(basePromotionValue, fareValue) + airTaxExemptAmount + fuelSurchargeExemptAmount
}

function bestCombinationOfPromotionsIncludingEpd (
  promotions,
  promoThatMustBeIncluded
) {
  const epd = promotions.find(p => p.epd)
  const bestCombinationWithoutEpd = bestCombinationOfPromotions(
    promotions.filter(p => !p.epd),
    promoThatMustBeIncluded
  )
  const bestCombinationWithEpd = bestCombinationOfPromotions(
    promotions.filter(p => !p.epd && p.epdCombinable),
    promoThatMustBeIncluded
  )
  if (epd) {
    bestCombinationWithEpd.push(epd)
  }

  if (
    epd &&
    sum(bestCombinationWithEpd.map(p => p.amount)) >
      sum(bestCombinationWithoutEpd.map(p => p.amount))
  ) {
    return bestCombinationWithEpd
  } else {
    return bestCombinationWithoutEpd
  }
}

function isPromotionEpd (promotion) {
  return promotion.epd || (promotion.code || '').toLowerCase() === 'epd'
}

function isPromotionReferral (promotion) {
  return ['ADV', 'REF'].includes(promotion.promotionSubtype)
}

function referralsPromoCombinability (promotions, promotion) {
  if (isPromotionReferral(promotion)) {
    return promotions.filter(promo => promo.promotionSubtype === promotion.promotionSubtype)
  }
}

function notCombinableWith (promotions, promotion) {
  if (isPromotionEpd(promotion)) {
    return promotions.filter(p => !p.epdCombinable)
  }

  const combinable = bestCombinationOfPromotionsIncludingEpd(
    promotions,
    promotion
  )
  const epd = promotions.find(isPromotionEpd)
  let notCombinable = promotions.filter(
    p =>
      !combinable.find(
        combinablePromotion => combinablePromotion.code === p.code
      )
  )

  if (epd && !promotion.epdCombinable) {
    notCombinable.push(epd)
  }

  if(isPromotionReferral(promotion)) {
    notCombinable.push(...referralsPromoCombinability(promotions, promotion))
  }

  return notCombinable
}

function bestCombinationOfPromotions (promotions, promoThatMustBeIncluded) {
  const allPromotions = promoThatMustBeIncluded &&
    promoThatMustBeIncluded.combinabilityGroups
    ? promotions.concat([promoThatMustBeIncluded])
    : promotions
  var combinableWithAll = allPromotions.filter(p => p.combinable)
  var nonCombinablePromotionGroupsByAmount = groupsOfCombinablePromotionsInOrderOfAmount(
    allPromotions.filter(p => !p.combinable)
  ).filter(g => {
    return (
      !promoThatMustBeIncluded ||
      promoThatMustBeIncluded.combinable ||
      g.promotions.find(p => p === promoThatMustBeIncluded)
    )
  })

  if (nonCombinablePromotionGroupsByAmount.length == 0) {
    return combinableWithAll
  } else {
    var cheapestNonCombinableGroupAmount = nonCombinablePromotionGroupsByAmount.reverse()[
      0
    ].amount
    var firstOfCheapestNonCombinableGroups = nonCombinablePromotionGroupsByAmount
      .reverse()
      .filter(g => g.amount === cheapestNonCombinableGroupAmount)[0]
    var nonCombinablePromotionsToApply =
      firstOfCheapestNonCombinableGroups.promotions

    return nonCombinablePromotionsToApply.concat(
      combinableWithAll.filter(
        combinablePromo =>
          !nonCombinablePromotionsToApply.find(
            p => p.code == combinablePromo.code
          )
      )
    )
  }
}

function groupsOfCombinablePromotionsInOrderOfAmount (promotions) {
  var promotionsGroupedByCombinabilityGroupId = promotions.reduce(
    (groups, promotion) => {
      promotion.combinabilityGroups.forEach(g => {
        groups[g.id] = groups[g.id] || []
        groups[g.id].push(promotion)
      })
      if (promotion.combinabilityGroups.length == 0 && !promotion.combinable) {
        groups[promotion.code] = [promotion]
      }
      return groups
    },
    {}
  )

  var groupsWithTotalAmount = Object.keys(
    promotionsGroupedByCombinabilityGroupId
  ).map(id => {
    var group = promotionsGroupedByCombinabilityGroupId[id]
    return {
      id,
      promotions: group,
      amount: sum(group.map(p => p.amount)),
    }
  })
  return sortBy(groupsWithTotalAmount, 'amount')
}

function promotionIsForTheTourProduct (promotion, departure) {
  return (
    promotion &&
    promotion.products &&
    promotion.products.find(p => p.productCode == departure.productCode)
  )
}

function applyMandatoryProductPromotions (
  passengers,
  mandatoryProducts,
  promotion
) {
  var mandatoryProductsForPromo = mandatoryProducts.filter(p => {
    return !!promotion.products.find(prod => prod.productCode == p.productCode)
  })

  var mandatoryProductsForPromoPrices = []
  if (mandatoryProductsForPromo.length > 0) {
    mandatoryProductsForPromoPrices = passengers.map(pax => ({
      amount: sum(mandatoryProductsForPromo.map(product => pax.type === 'adult' ? product.adultPrice : product.childPrice)),
    }))
  }

  var promoAmount = sum(
    mandatoryProductsForPromoPrices.map(product =>
      absoluteDiscountAmount(product.amount, promotion)
    )
  )
  return promoAmount || 0
}

function applyTransferPromotions (
  passengers,
  requestedTransferProducts,
  promotion
) {
  const transferProductsMatchingPromo = requestedTransferProducts
    ? requestedTransferProducts.filter(p => {
      return !!promotion.products.find(prod => prod.productCode == p.code)
    })
    : []

  const matchingProductsAmount = sum(transferProductsMatchingPromo.map(product => product.total))
  const promoAmount = matchingProductsAmount && absoluteDiscountAmount(matchingProductsAmount, promotion, passengers.length)

  return promoAmount || 0
}

function applyAccommodationPromotions (
  passengers,
  extraStays,
  promotion
) {
  const productsMatchingPromo = extraStays
    ? [
      extraStays.before,
      extraStays.after,
    ].filter(beforeAfter => {
      return beforeAfter &&
             beforeAfter.selectedHotel &&
             beforeAfter.selectedHotel.code &&
             !!promotion.products.find(prod => prod.productCode == beforeAfter.selectedHotel.code)
    })
    : []

  const matchingProductsAmount = sum(productsMatchingPromo.map(product => product.total))
  const promoAmount = matchingProductsAmount && absoluteDiscountAmount(matchingProductsAmount, promotion, passengers.length)
  return promoAmount || 0
}

function promotionsIncludingTravelProtection (
  priceBreakdownOptions,
  passengers,
  promotionsNotIncludingTravelProtection
) {
  // We have to consider travel protection promos after everything else has been applied because other promos/discounts apply first,
  // before the correct travel protection product can be selected. This can affect both the applicability of the promotion and it's value (if percent).
  //
  // To stop this getting prohibitively complex we assume only one promotion for a Travel Protection product at a time and we don't
  // consider whether it might offer better value that other promotions or EPDs, we only apply it if it can be combined with what has
  // already been applied.
  const travelProtectionPromo = breakdownTravelProtectionPromotion(
    priceBreakdownOptions,
    passengers
  )

  const existingPromotions = clone(promotionsNotIncludingTravelProtection)

  if (travelProtectionPromo) {
    var epd = existingPromotions.applied.find(p => p.epd)
    var cantCombineWithPromos =
      !travelProtectionPromo.combinable &&
      existingPromotions.applied.find(p => !p.combinable)
    var cantCombineWithEpd = !travelProtectionPromo.epdCombinable && epd

    if (!cantCombineWithPromos && !cantCombineWithEpd) {
      existingPromotions.applied.push(travelProtectionPromo)
      existingPromotions.amount += travelProtectionPromo.amount
    }
  }

  return existingPromotions
}

function breakdownTravelProtectionPromotion (priceBreakdownOptions, passengers) {
  if (priceBreakdownOptions.promotions) {
    const {promotions, departure, bookingDate} = priceBreakdownOptions
    var pricedPromotions = promotions
      .filter(promotion => productForPromoAddedToOrder({promotion, passengers, departure}))
      .map(promotion => {
        var amountPerPassenger = calculateTravelProtectionPromoAmountPerPassenger(
          passengers,
          promotion
        )

        const promoAmount = sum(amountPerPassenger)

        if (!promoAmount) return

        return {
          ...promotion,
          ...calculatePaymentDates(promotion, departure, bookingDate),
          amountPerPassenger,
          amount: promoAmount,
        }
      })
      .filter(Boolean)

    return sortBy(pricedPromotions, 'amount').reverse()[0]
  }
}

function calculateTravelProtectionPromoAmountPerPassenger (
  passengers,
  promotion
) {
  var passengersWithTravelProtectionMatchingPromo = passengers.filter(p => {
    return (
      p.requestedTravelProtection &&
      !!promotion.products.find(
        prod => prod.productCode == p.requestedTravelProtection.code
      )
    )
  })

  return passengersWithTravelProtectionMatchingPromo.map(p =>
    absoluteDiscountAmount(p.requestedTravelProtection.amount, promotion)
  )
}

function productForPromoAddedToOrder ({
  promotion,
  passengers,
  transfers,
  departure,
  extraStays = {},
}) {
  if (promotion.isAirPromotion) return true

  const addedTravelProtectionCodes = passengers
    .filter(p => {
      return p.requestedTravelProtection
    })
    .map(p => {
      return p.requestedTravelProtection.code
    })
  const addedProductCodes = (transfers || [])
    .map(p => p.code)
    .concat(addedTravelProtectionCodes)
    .concat([departure.productCode])
    .concat([
      extraStays.after && extraStays.after.selectedHotel.code,
      extraStays.before && extraStays.before.selectedHotel.code,
    ].filter(x => x))
    .concat(departure.prices.mandatoryProducts.map(p => p.productCode))

  return promotion.products.some(
    p => addedProductCodes.indexOf(p.productCode) > -1
  )
}
